import * as z from 'zod'
import { reportDrift } from './drift'
import { isPlainObject } from './json'
import { attachRaw } from './raw'
import { attachUnknown, type UnknownPath } from './unknown'

declare const unrecognizedBrand: unique symbol

/** A server value this client has no member for. Opt-in via `openEnum` / `openUnion` (D6). */
export type Unrecognized = string & { readonly [unrecognizedBrand]: true }

export function unrecognized(value: string): Unrecognized {
  return value as Unrecognized
}

type ParseState = {
  unknown: UnknownPath[]
  degraded: boolean
}

const stateStack: ParseState[] = []
const prefixStack: string[] = []

function currentState(): ParseState | undefined {
  return stateStack[stateStack.length - 1]
}

function currentPath(): string {
  return prefixStack[prefixStack.length - 1] ?? ''
}

function childPath(key: string): string {
  const parent = currentPath()
  return parent ? `${parent}.${key}` : key
}

function note(path: string, degrade: boolean): void {
  const state = currentState()
  if (!state) {
    return
  }

  if (path && !state.unknown.includes(path)) {
    state.unknown.push(path)
  }

  if (degrade) {
    state.degraded = true
  }
}

function failParse(
  ctx: { addIssue?: (issue: { code: 'custom'; message: string }) => void },
  message: string
): void {
  ctx.addIssue?.({ code: 'custom', message })
}

export function tolerant<T extends z.ZodType>(
  schema: T,
  fallback: z.output<T>,
  options?: { degrade?: boolean }
): z.ZodType<z.output<T>> {
  return z.unknown().transform(value => {
    const parsed = schema.safeParse(value)
    if (parsed.success) {
      return parsed.data
    }

    note(currentPath(), options?.degrade === true)
    return fallback
  }) as z.ZodType<z.output<T>>
}

export function optionalTolerant<T extends z.ZodType>(
  schema: T
): z.ZodType<z.output<T> | undefined> {
  return z.unknown().transform(value => {
    if (value === undefined) {
      return undefined
    }

    const parsed = schema.safeParse(value)
    if (parsed.success) {
      return parsed.data
    }

    note(currentPath(), false)
    return undefined
  }) as z.ZodType<z.output<T> | undefined>
}

/** Missing stays empty. A present value that does not fit is cleared, recorded, and degrades the entity. */
export function requiredTolerant<T extends z.ZodType>(
  schema: T
): z.ZodType<z.output<T> | undefined> {
  return z.unknown().transform(value => {
    if (value === undefined) {
      return undefined
    }

    const parsed = schema.safeParse(value)
    if (parsed.success) {
      return parsed.data
    }

    note(currentPath(), true)
    return undefined
  }) as z.ZodType<z.output<T> | undefined>
}

function enumMembers<T extends Record<string, string>>(enumObj: T): [string, ...string[]] {
  const values = [...new Set(Object.values(enumObj))]
  if (values.length === 0) {
    throw new Error('FCDC enum schema requires at least one member')
  }

  return values as [string, ...string[]]
}

export function enumWithFallback<T extends Record<string, string>>(
  enumObj: T,
  fallback: T[keyof T]
): z.ZodType<T[keyof T]> {
  const members = enumMembers(enumObj)
  const schema = z.enum(members)

  return z.unknown().transform(value => {
    if (value === undefined) {
      return fallback
    }

    const parsed = schema.safeParse(value)
    if (parsed.success) {
      return parsed.data as T[keyof T]
    }

    note(currentPath(), false)
    return fallback
  }) as z.ZodType<T[keyof T]>
}

export function openEnum<T extends Record<string, string>>(
  enumObj: T,
  fallback: T[keyof T]
): z.ZodType<T[keyof T] | Unrecognized> {
  const members = new Set(enumMembers(enumObj))

  return z.unknown().transform(value => {
    if (value === undefined) {
      return fallback
    }

    if (typeof value === 'string' && members.has(value)) {
      return value as T[keyof T]
    }

    if (typeof value === 'string') {
      return unrecognized(value)
    }

    note(currentPath(), false)
    return fallback
  }) as z.ZodType<T[keyof T] | Unrecognized>
}

type UnionOutput<V extends Record<string, z.ZodType>> =
  | z.output<V[keyof V]>
  | (Record<string, unknown> & { type?: Unrecognized })
  | undefined

export function openUnion<D extends string, V extends Record<string, z.ZodType>>(
  discriminant: D,
  variants: V
): z.ZodType<UnionOutput<V>> {
  return z.unknown().transform(value => {
    if (value === undefined) {
      return undefined
    }

    if (!isPlainObject(value)) {
      note(currentPath(), false)
      return undefined
    }

    const tag = value[discriminant]
    const variant = typeof tag === 'string' ? variants[tag] : undefined
    if (!variant) {
      note(currentPath(), false)
      return {
        ...value,
        [discriminant]: typeof tag === 'string' ? unrecognized(tag) : unrecognized(''),
      }
    }

    const parsed = variant.safeParse(value)
    if (!parsed.success) {
      note(currentPath(), false)
      return {
        ...value,
        [discriminant]: typeof tag === 'string' ? unrecognized(tag) : tag,
      }
    }

    return parsed.data
  }) as z.ZodType<UnionOutput<V>>
}

export function objectSchema<Shape extends Record<string, z.ZodType>>(
  shape: Shape
): z.ZodType<{ [K in keyof Shape]: z.output<Shape[K]> }> {
  return z.unknown().transform((value, ctx) => {
    if (!isPlainObject(value)) {
      failParse(ctx, 'Expected object')
      return {} as { [K in keyof Shape]: z.output<Shape[K]> }
    }

    const output = {} as { [K in keyof Shape]: z.output<Shape[K]> }
    for (const key of Object.keys(shape)) {
      const path = childPath(key)
      prefixStack.push(path)
      try {
        const parsed = shape[key].safeParse(value[key])
        if (parsed.success) {
          if (parsed.data !== undefined) {
            output[key as keyof Shape] = parsed.data as z.output<Shape[keyof Shape]>
          }
        } else {
          note(path, false)
        }
      } finally {
        prefixStack.pop()
      }
    }

    return output
  }) as z.ZodType<{ [K in keyof Shape]: z.output<Shape[K]> }>
}

/** Array items are parsed on their own. A non-array records the current path and yields undefined. */
export function tolerantArray<T extends z.ZodType>(
  item: T
): z.ZodType<Array<z.output<T> | undefined> | undefined> {
  return z.unknown().transform(value => {
    if (value === undefined) {
      return undefined
    }

    if (!Array.isArray(value)) {
      note(currentPath(), false)
      return undefined
    }

    const parent = currentPath()
    return value.map((entry, index) => {
      const path = parent ? `${parent}.${index}` : String(index)
      prefixStack.push(path)
      try {
        const parsed = item.safeParse(entry)
        if (!parsed.success) {
          note(path, false)
          return undefined
        }
        return parsed.data
      } finally {
        prefixStack.pop()
      }
    })
  }) as z.ZodType<Array<z.output<T> | undefined> | undefined>
}

export function tolerantEnum<T extends Record<string, string>>(enumObj: T) {
  return optionalTolerant(z.enum(enumMembers(enumObj)))
}

export function parseEntity<S extends z.ZodType>(
  schema: S,
  raw: unknown,
  model = 'entity'
): z.output<S> {
  const state: ParseState = { unknown: [], degraded: false }
  stateStack.push(state)

  try {
    const parsed = schema.safeParse(raw)
    if (!parsed.success || !isPlainObject(parsed.data)) {
      state.degraded = true
      if (!state.unknown.includes('$')) {
        state.unknown.push('$')
      }
      reportDrift(model, state.unknown, true)
      const view = {}
      attachRaw(view, raw)
      attachUnknown(view, state.unknown, true)
      return view as z.output<S>
    }

    reportDrift(model, state.unknown, state.degraded)
    const view = parsed.data
    attachRaw(view, raw)
    attachUnknown(view, state.unknown, state.degraded)
    return view as z.output<S>
  } catch (error) {
    reportDrift(model, ['$'], true, error)
    const view = {}
    attachRaw(view, raw)
    attachUnknown(view, ['$'], true)
    return view as z.output<S>
  } finally {
    stateStack.pop()
    prefixStack.length = 0
  }
}
